import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import Brand from "@/components/Brand";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Cpu, Smartphone, Globe, ShieldCheck } from "lucide-react";

const LOGIN_VISUAL =
  "https://images.pexels.com/photos/30547584/pexels-photo-30547584.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FEATURE_PILLS = [
  { icon: Cpu, label: "DSS AI Draft Generator" },
  { icon: Smartphone, label: "Instant Android Builds" },
  { icon: Globe, label: "Live Client Websites" },
  { icon: ShieldCheck, label: "Private Role-Based Access" },
];

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.error || "");
  const [loading, setLoading] = useState(false);

  // Already signed in? Go straight to the Work Desk.
  useEffect(() => {
    if (user && typeof user === "object") navigate("/workdesk", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/workdesk");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 rounded-xl bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-cyan-600 dark:focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-500/20 dark:focus:ring-cyan-400/15 text-sm transition-all";
  const labelCls = "block text-xs uppercase font-semibold text-slate-500 dark:text-slate-400 code-font mb-2 tracking-wider";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0B0F19] text-slate-900 dark:text-white grid lg:grid-cols-2" data-testid="login-page">
      {/* Left — dark luxury tech visual panel */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#080C14] p-12">
        <img
          src={LOGIN_VISUAL}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#080C14]/85 via-[#0B0F19]/70 to-cyan-950/60" aria-hidden="true" />
        <div className="absolute inset-0 bg-grid-dark" aria-hidden="true" />
        <div
          className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-cyan-400/15 blur-3xl"
          aria-hidden="true"
        />

        <Link to="/" className="relative flex items-center" data-testid="login-brand">
          <Brand subtitle="Private Limited" onDark />
        </Link>

        <div className="relative max-w-md">
          <div className="text-xs code-font uppercase tracking-widest text-cyan-300/90 font-semibold">
            dreamssoft-platform.internal
          </div>
          <h1 className="mt-4 text-4xl xl:text-5xl font-extrabold tracking-tight leading-[1.05] text-white">
            One desk. Every project.
            <span className="block neon-text mt-1">Designed. Built. Shipped.</span>
          </h1>
          <p className="mt-5 text-sm text-slate-300/90 leading-relaxed">
            The Dreams Soft Solutions Work Desk turns a client brief into a live website, an installable
            Android app, branded documentation and a connected domain — all from one place.
          </p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {FEATURE_PILLS.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="flex items-center gap-2 px-3.5 py-2 rounded-full border border-white/10 bg-white/[0.06] backdrop-blur-md text-xs font-medium text-slate-200"
              >
                <Icon className="w-3.5 h-3.5 text-cyan-300" aria-hidden="true" />
                {label}
              </span>
            ))}
          </div>
        </div>

        <div className="relative flex items-center gap-2 text-[11px] code-font text-slate-400">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" />
          SECURE CHANNEL · CREDENTIALS ISSUED BY THE TEAM
        </div>
      </div>

      {/* Right — glassmorphic access card */}
      <div className="relative flex flex-col">
        <nav className="px-6 py-4 flex items-center justify-between lg:justify-end">
          <Link to="/" className="flex items-center lg:hidden">
            <Brand subtitle="Private Limited" />
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Link
              to="/"
              className="text-xs code-font text-slate-500 dark:text-slate-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors"
              data-testid="login-back-home"
            >
              &larr; Back to site
            </Link>
          </div>
        </nav>

        <div className="flex-1 flex items-center justify-center px-6 pb-16 pt-6">
          <div className="w-full max-w-md">
            <div className="glass-panel rounded-2xl p-8 shadow-2xl accent-bar">
              <div className="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-white/10 text-xs code-font text-slate-500 dark:text-slate-400">
                <span>dreamssoft-platform.internal</span>
                <span className="flex items-center gap-1.5 text-cyan-700 dark:text-cyan-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-pulse" aria-hidden="true" />
                  SECURE
                </span>
              </div>
              <h1 className="mt-6 text-2xl font-bold text-slate-900 dark:text-white tracking-tight" data-testid="login-heading">
                Work Desk Access
              </h1>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                Restricted to active clients and Dreams Soft Solutions engineers. Credentials are issued directly by the team.
              </p>

              {error && (
                <div className="mt-4 p-3 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-700/40 text-rose-700 dark:text-rose-300 text-xs code-font" data-testid="login-error">
                  {error}
                </div>
              )}

              <form onSubmit={submit} className="mt-6 space-y-5" data-testid="login-form">
                <div>
                  <label className={labelCls} htmlFor="login-email">Email</label>
                  <input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@company.com"
                    className={inputCls}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="login-password">Password</label>
                  <input
                    id="login-password"
                    data-testid="login-password-input"
                    type="password"
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className={inputCls}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-3.5 rounded-xl bg-gradient-to-r from-cyan-600 to-sky-600 dark:from-cyan-400 dark:to-sky-400 text-white dark:text-[#090D16] font-bold uppercase text-xs tracking-wider hover:from-cyan-700 hover:to-sky-700 dark:hover:from-cyan-300 dark:hover:to-sky-300 shadow-lg shadow-cyan-600/20 dark:shadow-cyan-400/20 transition-all disabled:opacity-60"
                  data-testid="login-submit-button"
                >
                  {loading ? "Verifying…" : "Enter Workspace"}
                </button>
              </form>

              <p className="mt-6 text-xs text-slate-500 dark:text-slate-400 code-font" data-testid="login-help-text">
                Need credentials? <Link to="/#contact" className="text-cyan-700 dark:text-cyan-300 hover:text-cyan-800 dark:hover:text-cyan-200">Request access via the project brief form</Link>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
